import { useAppSelector } from "app/store";
import { getDataReportTypes } from "pages/data/DataConfig";
import { useAllTeams } from "hooks/useSubTeams";
import { Team } from "models/schemas";
import { childScanFlag } from "components/team_settings/scans-enabled/definitions";

/**
 * Whether the caller is a critical user of the given team, read from listTeams() — the same source the
 * server resolves its write gate from, so the two agree.
 *
 * Deliberately not `state.teamSettings.isUserCriticalInTeam`: that is only ever set for the
 * header-selected team, so it answers the wrong question for a sub-team, and the per-team map beside it
 * is never populated.
 */
export const useIsCriticalInTeam = (teamName?: string): boolean => {
  const allTeams = useAllTeams();
  if (!teamName) {
    return false;
  }
  return (
    allTeams.find((team: Team) => team.teamId === teamName)?.isCritical ?? false
  );
};

export const useSelectedTeam = () => {
  const selectedTeam = useAppSelector(
    (state) => state.teamSettings.selectedTeamMainMetaData,
  );

  const isFileScanEnabled =
    selectedTeam?.enabledFeatures?.includes("FILE_SCAN") ?? false;

  const isFileScanAssessmentEnabled =
    selectedTeam?.enabledFeatures?.includes("FILE_SCAN_ASSESSMENT") ?? false;

  const isFileScanTaxAreaEnabled =
    selectedTeam?.enabledFeatures?.includes("FILE_SCAN_TAX_AREA") ?? false;

  const isFileScanPaymentEnabled =
    selectedTeam?.enabledFeatures?.includes("FILE_SCAN_PAYMENT") ?? false;

  const hasFeature = (flag: string): boolean =>
    selectedTeam?.enabledFeatures?.includes(flag) ?? false;

  const isFileScanAuditTaskGroupEnabled = hasFeature(
    childScanFlag("AUDIT", "TASKGROUP"),
  );

  const isFileScanAuditAssessmentEnabled = hasFeature(
    childScanFlag("AUDIT", "ASSESSMENT"),
  );

  const isFileScanAuditPaymentEnabled = hasFeature(
    childScanFlag("AUDIT", "PAYMENT"),
  );

  const isFileScanNoticeAssessmentEnabled = hasFeature(
    childScanFlag("NOTICE", "ASSESSMENT"),
  );

  const isFileScanNoticePaymentEnabled = hasFeature(
    childScanFlag("NOTICE", "PAYMENT"),
  );

  const isGenericAuditPlanningEnabled =
    selectedTeam?.enabledFeatures?.includes("GENERIC_AUDIT_PLANNING") ?? false;

  const isSellerInfoSearchWorkflowEnabled =
    selectedTeam?.enabledFeatures?.includes(
      "SELLER_INFORMATION_LOOKUP_WORKFLOW",
    ) ?? false;

  const isBulkUploadAuditsEnabled =
    selectedTeam?.enabledFeatures?.includes("AUDIT") ?? false;

  const isBulkUploadNoticesEnabled =
    selectedTeam?.enabledFeatures?.includes("NOTICE") ?? false;

  const isBulkUploadLitigationsEnabled =
    selectedTeam?.enabledFeatures?.includes("LITIGATION") ?? false;

  const isRollForwardEnabled =
    selectedTeam?.enabledFeatures?.includes("ROLL_FORWARD") ?? false;

  const isPaymentRollForwardEnabled =
    selectedTeam?.enabledFeatures?.includes("PAYMENT_ROLL_FORWARD") ?? false;

  const isAssistantFileAttachmentEnabled =
    selectedTeam?.enabledFeatures?.includes("ASSISTANT_FILE_ATTACHMENT") ??
    false;

  const isSupersededPaymentsEnabled =
    selectedTeam?.enabledFeatures?.includes("SUPERSEDED_PAYMENTS") ?? false;

  const isGaiaObjectsCriticalDeleteOnlyEnabled =
    selectedTeam?.enabledFeatures?.includes(
      "GAIAOBJECTS_CRITICAL_DELETE_ONLY",
    ) ?? false;

  const isGaiaKBIngestionEnabled =
    selectedTeam?.enabledFeatures?.includes("GAIA_KB_INGESTION") ?? false;

  const isKBApprovalEnabled =
    selectedTeam?.enabledFeatures?.includes("KB_APPROVAL") ?? false;

  const isKBApprovalDeletionEnabled =
    selectedTeam?.enabledFeatures?.includes("KB_APPROVAL_DELETION") ?? false;

  const isKBApprovalFilesEnabled =
    selectedTeam?.enabledFeatures?.includes("KB_APPROVAL_FILES") ?? false;

  const isFolderKBIngestionEnabled =
    selectedTeam?.enabledFeatures?.includes("FOLDER_KB_INGESTION") ?? false;

  const isAnswersKbEnabled =
    selectedTeam?.enabledFeatures?.includes("CHATBOT") ?? false;

  const isFileDescriptionEnabled =
    selectedTeam?.enabledFeatures?.includes("FILE_DESCRIPTION") ?? false;

  const isKBTagEnabled =
    selectedTeam?.enabledFeatures?.includes("KB_TAG") ?? false;

  const isDataInsightsEnabled =
    selectedTeam?.enabledFeatures?.includes("DATA_INSIGHTS") ?? false;

  const isAgentWorkEnabled =
    selectedTeam?.enabledFeatures?.includes("AGENT_WORK") ?? false;

  const isAiQueryEnabled =
    selectedTeam?.enabledFeatures?.includes("AI_QUERY_BUILDER") ?? false;

  const isMinervaKbEnabled =
    selectedTeam?.enabledFeatures?.includes("MINERVA_KB") ?? false;

  const isPaymentConfigurationEnabled =
    selectedTeam?.enabledFeatures?.includes("PAYMENT_CONFIGURATION") ?? false;

  const isDashboardEnabled =
    selectedTeam?.enabledFeatures?.includes("DASHBOARD") ?? false;

  const isEscalationReviewTemplatesEnabled =
    selectedTeam?.enabledFeatures?.includes("ESCALATION_REVIEW_TEMPLATES") ??
    false;

  const isProgressUpdateFilesEnabled =
    selectedTeam?.enabledFeatures?.includes("PROGRESS_UPDATE_FILES") ?? false;

  const isReopenWindowEnabled =
    selectedTeam?.enabledFeatures?.includes("REOPEN_WINDOW") ?? false;

  const isExchangeRatesEnabled =
    selectedTeam?.enabledFeatures?.includes("EXCHANGE_RATES") ?? false;

  const isAssessmentLinkedTasksEnabled =
    selectedTeam?.enabledFeatures?.includes("ASSESSMENT_LINKED_TASKS") ?? false;

  const isSubTasksEnabled =
    selectedTeam?.enabledFeatures?.includes("SUB_TASKS") ?? false;

  const isClosingRequirementsEnabled =
    (selectedTeam?.enabledFeatures?.includes("CLOSING_REQUIREMENTS") ??
      false) &&
    (isBulkUploadAuditsEnabled ||
      isBulkUploadNoticesEnabled ||
      isBulkUploadLitigationsEnabled);

  return {
    selectedTeam,
    isFileScanEnabled,
    isFileScanAssessmentEnabled,
    isFileScanTaxAreaEnabled,
    isFileScanPaymentEnabled,
    isFileScanAuditTaskGroupEnabled,
    isFileScanAuditAssessmentEnabled,
    isFileScanAuditPaymentEnabled,
    isFileScanNoticeAssessmentEnabled,
    isFileScanNoticePaymentEnabled,
    isGenericAuditPlanningEnabled,
    isSellerInfoSearchWorkflowEnabled,
    isBulkUploadAuditsEnabled,
    isBulkUploadNoticesEnabled,
    isBulkUploadLitigationsEnabled,
    isRollForwardEnabled,
    isPaymentRollForwardEnabled,
    isAssistantFileAttachmentEnabled,
    isSupersededPaymentsEnabled,
    isGaiaObjectsCriticalDeleteOnlyEnabled,
    isGaiaKBIngestionEnabled,
    isKBApprovalEnabled,
    isKBApprovalDeletionEnabled,
    isKBApprovalFilesEnabled,
    isFolderKBIngestionEnabled,
    isAnswersKbEnabled,
    isFileDescriptionEnabled,
    isKBTagEnabled,
    isDataInsightsEnabled,
    isAgentWorkEnabled,
    isAiQueryEnabled,
    isMinervaKbEnabled,
    isPaymentConfigurationEnabled,
    isDashboardEnabled,
    isEscalationReviewTemplatesEnabled,
    isProgressUpdateFilesEnabled,
    isReopenWindowEnabled,
    isExchangeRatesEnabled,
    isAssessmentLinkedTasksEnabled,
    isSubTasksEnabled,
    isClosingRequirementsEnabled,
  };
};

export const useWorkflowAvailability = () => {
  const teamName = useAppSelector((state) => state.audits.teamName);
  const headerSelectedTeam = useAppSelector(
    (state) => state.audits.headerSelectedTeam,
  );
  const isCriticalUser = useAppSelector(
    (state) => state.teamSettings.isUserCriticalInTeam,
  );
  const {
    isSellerInfoSearchWorkflowEnabled,
    isBulkUploadAuditsEnabled,
    isBulkUploadNoticesEnabled,
    isBulkUploadLitigationsEnabled,
    isPaymentRollForwardEnabled,
  } = useSelectedTeam();

  const hasWorkflowsAvailable = (parentId?: string) => {
    if (headerSelectedTeam === "guest") {
      return false;
    }
    const workflows = getDataReportTypes(
      teamName,
      isCriticalUser,
      parentId,
      isSellerInfoSearchWorkflowEnabled,
      isBulkUploadAuditsEnabled,
      isBulkUploadNoticesEnabled,
      isBulkUploadLitigationsEnabled,
      isPaymentRollForwardEnabled,
    );
    return workflows.length > 0;
  };

  return {
    hasWorkflowsAvailable,
  };
};

const CATEGORY_ALIASES: Record<string, string> = {
  audits: "audit",
  notices: "notice",
  litigations: "litigation",
  "team-files": "team",
};

/**
 * Gaia default document types for audit draft, notice draft, litigation draft,
 * payment files, assessment files, event files, and team files
 * Audit files and Task files can select any team document type they do not have default
 */
const GAIA_DEFAULTS: Record<string, string> = {
  audit: "Audit notices",
  notice: "Notice letters",
  litigation: "Notices",
  payments: "Payments",
  assessments: "Assessments",
  events: "Events",
  team: "Team",
};

export const useDefaultDocumentType = (category: string): string => {
  const defaultDocumentTypes = useAppSelector(
    (state) =>
      state.teamSettings.selectedTeamMainMetaData?.defaultDocumentTypes,
  );
  const normalized = CATEGORY_ALIASES[category] ?? category;
  return (
    defaultDocumentTypes?.[normalized] ?? GAIA_DEFAULTS[normalized] ?? category
  );
};
